import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';

const adjustDateForEasternTime = (gameDate, gameTime) => {
  const [day, month, year] = gameDate.split('/').map(Number);
  const eventYear = year || 2024;
  let [hour, minute] = gameTime.match(/(\d+):(\d+)(am|pm)/).slice(1, 3);
  hour = parseInt(hour);
  minute = parseInt(minute);
  const isPM = gameTime.includes('pm');
  if (isPM && hour < 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;
  const londonDate = new Date(Date.UTC(eventYear, month - 1, day, hour, minute));
  const easternDate = new Date(londonDate.getTime() - 5 * 60 * 60 * 1000);
  const adjustedDate = easternDate.getUTCHours() < 5
    ? new Date(easternDate.getTime() - 24 * 60 * 60 * 1000)
    : easternDate;
  return `${String(adjustedDate.getUTCMonth() + 1).padStart(2, '0')}/${String(adjustedDate.getUTCDate()).padStart(2, '0')}/${adjustedDate.getUTCFullYear()}`;
};

const parseDisplayDate = (s) => {
  if (!s || s === 'Unknown') return new Date(0);
  const [m, d, y] = s.split('/').map(Number);
  return new Date(y, m - 1, d);
};

const ITEMS_PER_PAGE = 20;

const ManagePicks = () => {
  const [picks, setPicks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState('pick_time');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState('All');
  const [resultFilter, setResultFilter] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add('history-page-bg');
    fetchPicks();
    return () => document.body.classList.remove('history-page-bg');
  }, []);

  const fetchPicks = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/data/get-my-picks-enriched`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const enriched = res.data.map((pick) => ({
        ...pick,
        pick_time: new Date(pick.pick_time),
        game: (pick.home_team && pick.away_team)
          ? `${pick.home_team} vs ${pick.away_team}`
          : 'Unknown Game',
        gameDate: (pick.game_date && pick.game_time)
          ? adjustDateForEasternTime(pick.game_date, pick.game_time)
          : 'Unknown',
      }));
      enriched.sort((a, b) => b.pick_time - a.pick_time);
      setPicks(enriched);
    } catch (err) {
      console.error('Error fetching picks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Derived data ─────────────────────────────────────────────────────── */

  const availableSports = useMemo(() => {
    const seen = new Set(picks.map(p => p.sport).filter(Boolean));
    return ['All', ...Array.from(seen).sort()];
  }, [picks]);

  const filtered = useMemo(() => {
    let result = picks;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        (p.game || '').toLowerCase().includes(q) ||
        (p.picked_team || '').toLowerCase().includes(q)
      );
    }
    if (sportFilter !== 'All') result = result.filter(p => p.sport === sportFilter);
    if (resultFilter === 'Win')     result = result.filter(p => p.result === 1);
    else if (resultFilter === 'Loss')    result = result.filter(p => p.result === 0);
    else if (resultFilter === 'Pending') result = result.filter(p => p.result === null);
    return result;
  }, [picks, search, sportFilter, resultFilter]);

  const stats = useMemo(() => {
    const wins    = filtered.filter(p => p.result === 1).length;
    const losses  = filtered.filter(p => p.result === 0).length;
    const pending = filtered.filter(p => p.result === null).length;
    const settled = wins + losses;
    const winRate = settled > 0 ? Math.round((wins / settled) * 100) : null;

    // Walk time-ordered settled picks to find streaks
    const timeSorted = [...filtered]
      .filter(p => p.result !== null)
      .sort((a, b) => a.pick_time - b.pick_time);

    let maxStreak = 0, curStreak = 0;
    for (const p of timeSorted) {
      if (p.result === 1) { curStreak++; maxStreak = Math.max(maxStreak, curStreak); }
      else curStreak = 0;
    }

    let currentStreak = 0, currentStreakType = null;
    for (const p of [...timeSorted].reverse()) {
      if (currentStreakType === null) currentStreakType = p.result;
      if (p.result === currentStreakType) currentStreak++;
      else break;
    }

    // Per-sport breakdown
    const bySport = {};
    for (const p of filtered) {
      if (!p.sport) continue;
      if (!bySport[p.sport]) bySport[p.sport] = { wins: 0, losses: 0, pending: 0 };
      if (p.result === 1)      bySport[p.sport].wins++;
      else if (p.result === 0) bySport[p.sport].losses++;
      else                     bySport[p.sport].pending++;
    }

    return { wins, losses, pending, settled, winRate, maxStreak, currentStreak, currentStreakType, bySport };
  }, [filtered]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (sortKey === 'result') {
      va = va === null ? -1 : va;
      vb = vb === null ? -1 : vb;
    }
    if (sortKey === 'gameDate') {
      va = parseDisplayDate(va);
      vb = parseDisplayDate(vb);
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  }), [filtered, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const page = sorted.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  /* ── Handlers ─────────────────────────────────────────────────────────── */

  const setFilter = useCallback((setter, value) => {
    setter(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const deletePick = useCallback(async (e, id) => {
    e.stopPropagation();
    setDeletingIds(prev => new Set(prev).add(id));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/data/delete-gamepick/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPicks(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting pick:', err);
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <span className="mp-sort-icon mp-sort-idle">⇅</span>;
    return <span className="mp-sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const formatPickTime = (d) =>
    d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const winRateColor =
    stats.winRate === null  ? 'var(--text-dim)' :
    stats.winRate >= 60     ? 'var(--green)'    :
    stats.winRate >= 40     ? 'var(--flame)'    : 'var(--red)';

  /* ── Render ───────────────────────────────────────────────────────────── */

  return (
    <BaseLayout>
      <div className="history-container">

        {/* Header */}
        <div className="history-header">
          <h2 className="history-title">Your Picks</h2>
          {!isLoading && (
            <span className="mp-total-badge">{picks.length} total pick{picks.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {isLoading ? (
          <FlameLoader text="Loading picks..." />
        ) : picks.length === 0 ? (
          <p className="no-data-text">You have not made any picks yet.</p>
        ) : (
          <>
            {/* ── Stats panel ─────────────────────────────────────────── */}
            <div className="mp-stats-grid">
              <div className="mp-stat-card">
                <div className="mp-stat-value" style={{ color: winRateColor }}>
                  {stats.winRate !== null ? `${stats.winRate}%` : '—'}
                </div>
                <div className="mp-stat-label">Win Rate</div>
              </div>
              <div className="mp-stat-card">
                <div className="mp-stat-value" style={{ color: 'var(--green)' }}>{stats.wins}</div>
                <div className="mp-stat-label">Wins</div>
              </div>
              <div className="mp-stat-card">
                <div className="mp-stat-value" style={{ color: 'var(--red)' }}>{stats.losses}</div>
                <div className="mp-stat-label">Losses</div>
              </div>
              <div className="mp-stat-card">
                <div className="mp-stat-value">{stats.pending}</div>
                <div className="mp-stat-label">Pending</div>
              </div>
              <div className="mp-stat-card">
                <div className="mp-stat-value">
                  {(resultFilter === 'Win' || resultFilter === 'Loss') ? '—' : stats.currentStreak > 0 ? (
                    <span style={{ color: stats.currentStreakType === 1 ? 'var(--green)' : 'var(--red)' }}>
                      {stats.currentStreakType === 1 ? 'W' : 'L'}{stats.currentStreak}
                    </span>
                  ) : '—'}
                </div>
                <div className="mp-stat-label">Current Streak</div>
              </div>
              <div className="mp-stat-card">
                <div className="mp-stat-value" style={{ color: 'var(--gold)' }}>
                  {(resultFilter === 'Win' || resultFilter === 'Loss') ? '—' : stats.maxStreak > 0 ? `W${stats.maxStreak}` : '—'}
                </div>
                <div className="mp-stat-label">Best Win Streak</div>
              </div>
            </div>

            {/* ── Per-sport breakdown ──────────────────────────────────── */}
            {Object.keys(stats.bySport).length > 1 && (
              <div className="mp-sport-breakdown">
                {Object.entries(stats.bySport)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([sport, s]) => {
                    const settledS = s.wins + s.losses;
                    const rate = settledS > 0 ? Math.round((s.wins / settledS) * 100) : null;
                    const rateColor =
                      rate === null   ? 'var(--text-dim)' :
                      rate >= 60      ? 'var(--green)'    :
                      rate >= 40      ? 'var(--flame)'    : 'var(--red)';
                    return (
                      <div
                        key={sport}
                        className={`mp-breakdown-card${sportFilter === sport ? ' active' : ''}`}
                        onClick={() => setFilter(setSportFilter, sportFilter === sport ? 'All' : sport)}
                        title={`Click to filter by ${sport}`}
                      >
                        <span className="mp-sport-pill">{sport}</span>
                        <span className="mp-breakdown-rate" style={{ color: rateColor }}>
                          {rate !== null ? `${rate}%` : '—'}
                        </span>
                        <span className="mp-breakdown-detail">
                          {s.wins}W&nbsp;{s.losses}L{s.pending > 0 ? ` ${s.pending}P` : ''}
                        </span>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* ── Filters ─────────────────────────────────────────────── */}
            <div className="mp-filters">
              <div className="mp-search-wrap">
                <input
                  className="mp-search-input"
                  type="text"
                  placeholder="Search by team or matchup…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                />
                {search && (
                  <button className="mp-search-clear" onClick={() => { setSearch(''); setCurrentPage(1); }}>✕</button>
                )}
              </div>
              <div className="mp-filter-row">
                <div className="mp-filter-section">
                  <span className="mp-filter-label">League</span>
                  <div className="mp-filter-group">
                    {availableSports.map(s => (
                      <button
                        key={s}
                        className={`mp-filter-pill${sportFilter === s ? ' active' : ''}`}
                        onClick={() => setFilter(setSportFilter, s)}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div className="mp-filter-section">
                  <span className="mp-filter-label">Result</span>
                  <div className="mp-filter-group">
                    {['All', 'Win', 'Loss', 'Pending'].map(r => (
                      <button
                        key={r}
                        className={`mp-filter-pill mp-result-pill-${r.toLowerCase()}${resultFilter === r ? ' active' : ''}`}
                        onClick={() => setFilter(setResultFilter, r)}
                      >{r}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Result count */}
            <div className="mp-result-count">
              {filtered.length !== picks.length
                ? `Showing ${filtered.length} of ${picks.length} picks`
                : `${filtered.length} pick${filtered.length !== 1 ? 's' : ''}`}
              {(search || sportFilter !== 'All' || resultFilter !== 'All') && (
                <button
                  className="mp-clear-filters"
                  onClick={() => { setSearch(''); setSportFilter('All'); setResultFilter('All'); setCurrentPage(1); }}
                >Clear filters</button>
              )}
            </div>

            {filtered.length === 0 ? (
              <p className="no-data-text" style={{ marginTop: '2rem' }}>No picks match your filters.</p>
            ) : (
              <>
                {/* ── Table ─────────────────────────────────────────────── */}
                <div className="mp-table-wrap">
                  <table className="mp-table">
                    <thead>
                      <tr>
                        {[
                          { key: 'sport',       label: 'Sport'     },
                          { key: 'game',        label: 'Matchup'   },
                          { key: 'gameDate',    label: 'Game Date' },
                          { key: 'picked_team', label: 'Your Pick' },
                          { key: 'pick_time',   label: 'Picked At' },
                          { key: 'result',      label: 'Result'    },
                          { key: null,          label: ''          },
                        ].map(({ key, label }) => (
                          <th
                            key={label}
                            className={key ? 'mp-th-sortable' : ''}
                            onClick={key ? () => handleSort(key) : undefined}
                          >
                            {label}{key && <SortIcon k={key} />}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {page.map((pick) => (
                        <tr
                          key={pick.id}
                          className="mp-row"
                          onClick={() => navigate(`/game/${pick.game_id}`)}
                        >
                          <td className="mp-td-sport">
                            <span className="mp-sport-pill">{pick.sport}</span>
                          </td>
                          <td className="mp-td-game">
                            <span>{pick.game}</span>
                            {pick.score && pick.score !== '-' && pick.result !== null && (
                              <div className="mp-score-line">{pick.score}</div>
                            )}
                          </td>
                          <td className="mp-td-date">{pick.gameDate}</td>
                          <td className={`mp-td-pick${pick.result === 1 ? ' mp-pick-won' : pick.result === 0 ? ' mp-pick-lost' : ''}`}>
                            {pick.picked_team}
                            {pick.result === 0 && pick.winning_team && (
                              <div className="mp-actual-winner">↑ {pick.winning_team} won</div>
                            )}
                          </td>
                          <td className="mp-td-time">{formatPickTime(pick.pick_time)}</td>
                          <td className="mp-td-result">
                            {pick.result === null && <span className="mp-result-pending">Pending</span>}
                            {pick.result === 1    && <span className="mp-result-win">Win</span>}
                            {pick.result === 0    && <span className="mp-result-loss">Loss</span>}
                          </td>
                          <td className="mp-td-action" onClick={(e) => e.stopPropagation()}>
                            {pick.result === null && (
                              <button
                                className="mp-delete-btn"
                                onClick={(e) => deletePick(e, pick.id)}
                                disabled={deletingIds.has(pick.id)}
                              >
                                {deletingIds.has(pick.id) ? '…' : 'Delete'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination ────────────────────────────────────────── */}
                {totalPages > 1 && (
                  <div className="mp-pagination">
                    <button
                      className="mp-page-btn"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >← Prev</button>
                    <span className="mp-page-info">Page {currentPage} of {totalPages}</span>
                    <button
                      className="mp-page-btn"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </BaseLayout>
  );
};

export default ManagePicks;
